import { useEffect, useState } from "react";
import Nav from "@/components/Nav";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { toast } from "sonner";

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/pricing").then(r => setTiers(r.data)).catch(() => toast.error("Pricing unavailable"));
  }, []);

  const checkout = async (tier) => {
    if (!user) { nav("/login"); return; }
    setBusy(tier.lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: tier.lookup_key,
        quantity: 1,
        origin_url: window.location.origin,
        user_id: user.id,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally { setBusy(null); }
  };

  return (
    <div className="grain min-h-screen bg-[#050505] text-white">
      <Nav />
      <div className="mx-auto max-w-7xl px-6 py-16">
        <div className="max-w-3xl">
          <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#E5FF00]">/ PRICING · CLEAR & BRUTAL</div>
          <h1 className="font-display text-5xl font-medium tracking-tighter md:text-6xl">Erase yourself.<br/>Choose your weapon.</h1>
          <p className="mt-6 max-w-xl text-zinc-400">
            All plans include the OBSIDIAN AI wizard, 400+ broker coverage, and encrypted evidence vault. Cancel anytime.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-0 border border-white/10 md:grid-cols-3">
          {tiers.map((t) => (
            <div key={t.lookup_key}
              data-testid={`tier-${t.lookup_key}`}
              className={`relative flex flex-col justify-between border-white/10 p-10 md:border-r ${t.featured ? "bg-[#E5FF00]/[0.04] border-y-2 border-y-[#E5FF00]" : ""} last:border-r-0`}>
              {t.featured && (
                <div className="absolute -top-3 left-10 border border-[#E5FF00] bg-[#E5FF00] px-2 py-1 font-mono text-[9px] uppercase tracking-[0.25em] text-black">
                  Most Popular
                </div>
              )}
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{t.interval}</div>
                <h3 className="mt-4 font-display text-3xl font-medium tracking-tight">{t.name}</h3>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-display text-5xl font-medium tracking-tighter">${t.price}</span>
                  <span className="font-mono text-xs text-zinc-500">/{t.interval === "one-time" ? "one-time" : t.interval}</span>
                </div>
                <p className="mt-3 text-sm text-zinc-400">{t.description}</p>
                <ul className="mt-8 space-y-3">
                  {t.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#E5FF00]" strokeWidth={2} />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
              <Button data-testid={`buy-${t.lookup_key}`} disabled={busy === t.lookup_key} onClick={() => checkout(t)}
                className={`btn-cta mt-10 h-12 w-full rounded-none font-mono text-[11px] uppercase tracking-[0.25em] ${
                  t.featured
                    ? "border border-[#E5FF00] bg-[#E5FF00] text-black hover:bg-transparent hover:text-[#E5FF00]"
                    : "border border-white/30 bg-transparent text-white hover:border-[#E5FF00] hover:text-[#E5FF00]"
                }`}>
                {busy === t.lookup_key ? "Redirecting…" : `Get ${t.name} →`}
              </Button>
            </div>
          ))}
        </div>

        <p className="mt-10 max-w-xl font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
          Payment secured by Stripe · Test card 4242 4242 4242 4242 · Tax handled automatically
        </p>
      </div>
    </div>
  );
}
